import type { Paged } from '@/services/customer.service'

/**
 * Pure tree logic of the Organization section (TO-389 organization-admin-ui
 * REQ-002/003/004, design §2–3). Everything runs over the in-memory unit list,
 * iteratively, so a 2,000-unit (or very deep) tree stays fast and stack-safe.
 */

export interface TreeUnit {
	id: string
	code: string
	name: string
	parentId: string | null
}

export interface OrgTreeModel<T extends TreeUnit = TreeUnit> {
	byId: Map<string, T>
	/** Child ids per parent id, in the input (API code) order. */
	children: Map<string, string[]>
	roots: string[]
	/** All units below each unit, at any depth and status. */
	descendantCount: Map<string, number>
	/** Normalized `code name` per unit, for search. */
	searchKey: Map<string, string>
}

export interface TreeFilter {
	matches: Set<string>
	/** Matches plus their ancestor chains. */
	visible: Set<string>
}

export interface FlatRow {
	id: string
	depth: number
	hasChildren: boolean
	expanded: boolean
}

/**
 * Load every page: page 1 first (its `totalPages` sizes the rest), then the
 * remaining pages through a pool of `concurrency` workers. Any failure rejects
 * the whole load — no partial tree.
 */
export async function loadAllPages<T>(fetchPage: (page: number) => Promise<Paged<T>>, concurrency = 4): Promise<T[]> {
	const first = await fetchPage(1)
	const results: T[][] = [first.data]
	let next = 2
	const worker = async () => {
		while (next <= first.totalPages) {
			const page = next++
			results[page - 1] = (await fetchPage(page)).data
		}
	}
	const workers = Math.min(concurrency, Math.max(0, first.totalPages - 1))
	await Promise.all(Array.from({ length: workers }, worker))
	return results.flat()
}

/** Lowercase, strip combining diacritics (NFD), trim. */
export const normalizeSearch = (value: string): string =>
	value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

export function buildTree<T extends TreeUnit>(units: T[]): OrgTreeModel<T> {
	const byId = new Map(units.map((u) => [u.id, u]))
	const children = new Map<string, string[]>()
	const roots: string[] = []
	for (const u of units) {
		if (u.parentId !== null && byId.has(u.parentId)) {
			const siblings = children.get(u.parentId)
			if (siblings) siblings.push(u.id)
			else children.set(u.parentId, [u.id])
		} else {
			roots.push(u.id)
		}
	}
	const descendantCount = new Map<string, number>()
	// Iterative post-order: a node's count is final once all its children are.
	const stack: Array<{ id: string; visited: boolean }> = roots.map((id) => ({ id, visited: false }))
	while (stack.length > 0) {
		const top = stack.pop() as { id: string; visited: boolean }
		const kids = children.get(top.id) ?? []
		if (top.visited) {
			descendantCount.set(
				top.id,
				kids.reduce((sum, kid) => sum + 1 + (descendantCount.get(kid) ?? 0), 0),
			)
		} else {
			stack.push({ id: top.id, visited: true })
			for (const kid of kids) stack.push({ id: kid, visited: false })
		}
	}
	const searchKey = new Map(units.map((u) => [u.id, `${normalizeSearch(u.code)}\u0000${normalizeSearch(u.name)}`]))
	return { byId, children, roots, descendantCount, searchKey }
}

/** The ids of a unit's ancestors, nearest first. */
export function ancestorsOf(model: OrgTreeModel, id: string): string[] {
	const chain: string[] = []
	let parent = model.byId.get(id)?.parentId ?? null
	while (parent !== null && model.byId.has(parent) && !chain.includes(parent)) {
		chain.push(parent)
		parent = model.byId.get(parent)?.parentId ?? null
	}
	return chain
}

/** Units whose code or name contains the query, plus their ancestors; null for an empty query. */
export function filterTree(model: OrgTreeModel, query: string): TreeFilter | null {
	const needle = normalizeSearch(query)
	if (needle === '') return null
	const matches = new Set<string>()
	const visible = new Set<string>()
	for (const [id, key] of model.searchKey) {
		const [code = '', name = ''] = key.split('\u0000')
		if (!code.includes(needle) && !name.includes(needle)) continue
		matches.add(id)
		let current: string | null = id
		while (current !== null && !visible.has(current)) {
			visible.add(current)
			const parent: string | null = model.byId.get(current)?.parentId ?? null
			current = parent !== null && model.byId.has(parent) ? parent : null
		}
	}
	return { matches, visible }
}

/**
 * The rendered rows, depth-first in sibling order. Without a filter only
 * expanded branches open; with one, only visible units show and every visible
 * unit with visible children is open.
 */
export function flattenTree(model: OrgTreeModel, expanded: ReadonlySet<string>, filter?: TreeFilter | null): FlatRow[] {
	const rows: FlatRow[] = []
	const keep = (id: string) => !filter || filter.visible.has(id)
	const stack: Array<{ id: string; depth: number }> = []
	const pushChildren = (ids: string[], depth: number) => {
		for (let i = ids.length - 1; i >= 0; i--) {
			const id = ids[i] as string
			if (keep(id)) stack.push({ id, depth })
		}
	}
	pushChildren(model.roots, 0)
	while (stack.length > 0) {
		const { id, depth } = stack.pop() as { id: string; depth: number }
		const kids = (model.children.get(id) ?? []).filter(keep)
		const isOpen = kids.length > 0 && (filter ? true : expanded.has(id))
		rows.push({ id, depth, hasChildren: kids.length > 0, expanded: isOpen })
		if (isOpen) pushChildren(kids, depth + 1)
	}
	return rows
}
